// ---------------------------------------------------------------------------
// GraderEvaluateFormColumn — the left-hand form for the grader Evaluate tab:
// the pinned grader-under-test card (the current grader, adapted as the subject
// under test via the grader-as-agent pattern) plus the reused eval-template
// cards (query/data set, evaluating graders, variables, concurrency).
// Presentational — all state lives in GraderEvaluateTab and is threaded in via
// the form-field hook bundles.
// ---------------------------------------------------------------------------

import { Input } from '@fluentui/react-components';

import {
  classifyBindingFlowDiagnostics,
  type BindingFlowAnalysis,
} from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/bindingFlow.utils.js';
import { BindingFlowPanel } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/components/BindingFlowPanel/BindingFlowPanel.js';
import { ConcurrencyCard } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/components/ConcurrencyCard/ConcurrencyCard.js';
import { GradersList } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/components/GradersList/GradersList.js';
import { QueryDataSetCards } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/components/QueryDataSetCards/QueryDataSetCards.js';
import type {
  GraderSummary,
  TaskDraft,
} from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.types.js';
import type { FileSource } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useFileSource.js';
import type { UseFormFieldsResult } from '../../../EvaluationTemplatesPage/EvaluationTemplateForm/hooks/useFormFields.js';

import { useStyles } from './GraderEvaluateFormColumn.style.js';

export interface GraderEvaluateFormColumnProps {
  graderName: string;
  disabled: boolean;
  /** Model the graders run with at eval time (bound to the GRADER_MODEL var). */
  graderModel: string;
  onGraderModelChange: (value: string) => void;
  /** Per-variable runtime bindings (name → $expr), one row per declared variable. */
  bindings: { name: string; expr: string }[];
  bindingFlow: BindingFlowAnalysis;
  onBindingChange: (name: string, expr: string) => void;
  fields: UseFormFieldsResult;
  queryFile: FileSource;
  dataFile: FileSource;
  task: TaskDraft;
  uniqueGraders: GraderSummary[];
  gradersLoading: boolean;
  onAddGraderRow: (name: string) => void;
  onRemoveGraderRow: (rowId: string) => void;
  runError: string | null;
}

export function GraderEvaluateFormColumn({
  graderName,
  disabled,
  graderModel,
  onGraderModelChange,
  bindings,
  bindingFlow,
  onBindingChange,
  fields,
  queryFile,
  dataFile,
  task,
  uniqueGraders,
  gradersLoading,
  onAddGraderRow,
  onRemoveGraderRow,
  runError,
}: GraderEvaluateFormColumnProps) {
  const styles = useStyles();
  const diagnostics = classifyBindingFlowDiagnostics(bindingFlow);

  return (
    <div className={styles.formColumn}>
      <div className={styles.cards}>
        {/* Pinned grader under test — fixed to this page, not selectable. */}
        <section className={styles.card} aria-labelledby="grader-eval-subject">
          <div id="grader-eval-subject" className={styles.cardLabel}>
            Grader under test
          </div>
          <div className={styles.cardBody}>
            <span className={styles.graderName}>{graderName}</span>
            <span className={styles.graderHint}>
              Evaluated as the subject under test (grader-as-agent). Pick a
              grader below to score it.
            </span>
          </div>
        </section>

        {/* Judge model — supplied to the graders at run time via GRADER_MODEL. */}
        <section className={styles.card} aria-labelledby="grader-eval-model">
          <label
            id="grader-eval-model"
            htmlFor="grader-eval-model-input"
            className={styles.cardLabel}
          >
            Grader model
          </label>
          <Input
            id="grader-eval-model-input"
            value={graderModel}
            placeholder="gpt-4o"
            disabled={disabled}
            onChange={(_, d) => onGraderModelChange(d.value)}
          />
          <span className={styles.graderHint}>
            The model the grader-under-test and the evaluating grader run with
            for this evaluation.
          </span>
        </section>

        <fieldset disabled={disabled} className={styles.fieldsetReset}>
          <QueryDataSetCards
            viewOnly={false}
            querySetMode={fields.querySetMode}
            onQuerySetModeChange={fields.setQuerySetMode}
            queryInlineText={fields.queryInlineText}
            onQueryInlineTextChange={fields.setQueryInlineText}
            queryFileName={queryFile.name}
            queryFileItemCount={queryFile.items?.length ?? null}
            queryFileAnalysis={queryFile.analysis}
            queryFileError={queryFile.error}
            queryErrors={diagnostics.querySet}
            onQueryFile={queryFile.handle}
            dataSetEnabled={fields.dataSetEnabled}
            onDataSetEnabledChange={fields.setDataSetEnabled}
            dataSetMode={fields.dataSetMode}
            onDataSetModeChange={fields.setDataSetMode}
            dataInlineText={fields.dataInlineText}
            onDataInlineTextChange={fields.setDataInlineText}
            dataFileName={dataFile.name}
            dataFileItemCount={dataFile.items?.length ?? null}
            dataFileAnalysis={dataFile.analysis}
            dataFileError={dataFile.error}
            dataErrors={diagnostics.dataSet}
            onDataFile={dataFile.handle}
            dataSetExistingId={fields.dataSetExistingId}
            dataSetExistingVersion={fields.dataSetExistingVersion}
            onDataSetExistingChange={(id, version) => {
              if (id !== fields.dataSetExistingId)
                fields.setDataSetFileSelector([]);
              fields.setDataSetExistingId(id);
              fields.setDataSetExistingVersion(version);
            }}
            dataSetFileSelector={fields.dataSetFileSelector}
            onDataSetFileSelectorChange={fields.setDataSetFileSelector}
          />

          <section
            className={styles.card}
            aria-labelledby="grader-eval-graders"
          >
            <div id="grader-eval-graders" className={styles.cardLabel}>
              Evaluating graders
            </div>
            <span className={styles.graderHint}>
              Only numeric comparison graders can compare predicted scores with
              golden expected scores.
            </span>
            <GradersList
              task={task}
              uniqueGraders={uniqueGraders}
              gradersLoading={gradersLoading}
              viewOnly={false}
              onAddGraderRow={onAddGraderRow}
              onRemoveGraderRow={onRemoveGraderRow}
            />
          </section>

          <BindingFlowPanel
            analysis={bindingFlow}
            bindings={bindings}
            onBindingChange={onBindingChange}
            onVariableUpdate={fields.updateVariable}
            onVariableAdd={fields.addVariable}
            onVariableRemove={fields.removeVariable}
            disabled={disabled}
          />

          <ConcurrencyCard
            workersText={fields.workersText}
            onWorkersChange={fields.setWorkersText}
            parallelism={fields.parallelism}
            onParallelismChange={fields.setParallelism}
          />

          {runError !== null && (
            <div className={styles.errorText} role="alert">
              {runError}
            </div>
          )}
        </fieldset>
      </div>
    </div>
  );
}
